import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, List, ListItem, ListItemText, TextField } from '@mui/material';
import { getAdminAdminsApi, updateAdminViewBoostApi } from '../../api';
import ConfirmModal from '../common/ConfirmModal';

const AdminManageModal = ({ open, onClose }) => {
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchAdmins = async () => {
      try {
        setError('');
        const res = await getAdminAdminsApi();
        const items = res?.data || res || [];
        if (!cancelled) setAdmins(Array.isArray(items) ? items : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || '관리자 목록을 불러오지 못했습니다.');
      }
    };
    fetchAdmins();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
        }}
        onClick={onClose}
      >
        <Paper
          elevation={3}
          sx={{
            width: 360,
            maxWidth: '90%',
            p: 3,
            pt: 3,
            pb: 3.5,
            textAlign: 'center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            관리자 조회수 관리
          </Typography>

          {!error && admins.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              관리자 계정이 없습니다.
            </Typography>
          )}

          <List dense sx={{ textAlign: 'left', mb: 3, maxHeight: 400, overflowY: 'auto' }}>
            {admins.map((admin, idx) => (
              <React.Fragment key={admin.userId}>
                <ListItem sx={{ px: 0, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ListItemText
                    primaryTypographyProps={{ sx: { fontWeight: 700 } }}
                    primary={`${admin.name} (${admin.loginId})`}
                    sx={{ m: 0 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="number"
                      label="조회수"
                      value={admin.viewBoost ?? 1}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAdmins((prev) =>
                          prev.map((a) =>
                            a.userId === admin.userId ? { ...a, viewBoost: v ? Number(v) : '' } : a,
                          ),
                        );
                      }}
                      inputProps={{ min: 1 }}
                      sx={{ width: 100 }}
                    />
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onClose}
              sx={{
                py: 1.2,
                fontSize: 16,
                fontWeight: 600,
                borderColor: '#d0d0d0',
                color: '#333',
                '&:hover': {
                  borderColor: '#b0b0b0',
                  bgcolor: '#f8f8f8',
                },
              }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              fullWidth
              sx={{
                bgcolor: '#0d47a1',
                fontWeight: 600,
                py: 1.2,
                fontSize: 16,
                '&:hover': {
                  bgcolor: '#0b3a81',
                },
              }}
              disabled={savingId !== null}
              onClick={() => {
                for (const admin of admins) {
                  const boost = Number(admin.viewBoost);
                  if (!Number.isInteger(boost) || boost <= 0) {
                    setError('view_boost는 1 이상의 정수여야 합니다.');
                    return;
                  }
                }
                setConfirmOpen(true);
              }}
            >
              저장
            </Button>
          </Box>
        </Paper>
      </Box>

      <ConfirmModal
        open={confirmOpen}
        message="저장하시겠습니까?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          try {
            setSavingId(-1);
            setError('');
            await Promise.all(
              admins.map((admin) =>
                updateAdminViewBoostApi(admin.userId, Number(admin.viewBoost || 1)),
              ),
            );
            setConfirmOpen(false);
            onClose?.();
          } catch (e) {
            setError(e?.message || '저장에 실패했습니다.');
          } finally {
            setSavingId(null);
          }
        }}
      />
    </>
  );
};

export default AdminManageModal;

